import React, { FC, useState } from "react";
import { Button } from "@src/lib/ui/Button";
import PlusIcon from "@src/lib/icons/18px/plus";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogHeader,
} from "@src/lib/ui/Dialog";
import Text from "@src/lib/ui/Text";
import { useTracking } from "@src/features/app/hooks/useTracking";

type Props = {
  variant?: "default" | "outline";
};

type IntegrationKey = "googleBusiness" | "ovation";
type DialogStep = "integration_options" | "integration_detail";
type DialogCloseReason = "dialog_dismissed" | "integration_connect";

type IntegrationOptionConfig = {
  key: IntegrationKey;
  logo: string;
  title: string;
  description: string;
  detailTitle: string;
  detailDescription: string;
  connectLabel: string;
  oauthPath?: string;
  emailTo?: string;
  emailSubject?: string;
  emailBody?: string;
};

const INTEGRATION_OPTIONS: IntegrationOptionConfig[] = [
  {
    key: "googleBusiness",
    logo: "/images/google-business-profile-logo.svg",
    title: "Google Business Profile",
    description: "Import Google reviews from connected locations.",
    detailTitle: "Connect Google Business Profile",
    detailDescription:
      "To integrate with Google Business Profile, you will be redirected to Google to approve access. Once connected, Opus can import guest reviews from your connected locations and use them to identify recurring feedback themes and training opportunities.",
    connectLabel: "Continue to Google",
    oauthPath: "/v1/google-business/oauth/start",
  },
  {
    key: "ovation",
    logo: "/images/ovation-logo.png",
    title: "Ovation",
    description: "Import Ovation guest feedback across your locations.",
    detailTitle: "Connect Ovation",
    detailDescription:
      'In order to connect Ovation you must authorize that Opus can access your data. This is done via email. Press "Generate email" to open a prefilled email that you can send to the Opus team.',
    connectLabel: "Generate email",
    emailTo: "support@opus.so",
    emailSubject: "Ovation integration request",
    emailBody:
      "Hi,\n\nWe would like to integrate Ovation with Opus Guest Feedback. Can you help us get the process started?\n\nThanks,",
  },
];

const AddGuestFeedbackIntegrationButton: FC<Props> = ({
  variant = "default",
}) => {
  const { trackEvent } = useTracking();
  const [open, setOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] =
    useState<IntegrationOptionConfig | null>(null);

  const getDialogStep = (): DialogStep => {
    if (selectedIntegration) return "integration_detail";
    return "integration_options";
  };

  const handleClose = (reason: DialogCloseReason = "dialog_dismissed") => {
    trackEvent("guest_feedback_integration_dialog_closed", {
      reason,
      step: getDialogStep(),
      selectedIntegrationKey: selectedIntegration?.key ?? null,
    });
    setSelectedIntegration(null);
    setOpen(false);
  };

  const handleSelectIntegration = (option: IntegrationOptionConfig) => {
    trackEvent("guest_feedback_integration_option_selected", {
      integrationKey: option.key,
      integrationTitle: option.title,
    });
    trackEvent("guest_feedback_integration_detail_viewed", {
      source: "integration_option_selected",
      integrationKey: option.key,
      integrationTitle: option.title,
    });
    setSelectedIntegration(option);
  };

  const handleConnect = (option: IntegrationOptionConfig) => {
    const connectionMethod = option.emailTo
      ? "email"
      : option.oauthPath
        ? "oauth"
        : "unknown";
    trackEvent("guest_feedback_integration_connect_clicked", {
      integrationKey: option.key,
      integrationTitle: option.title,
      connectionMethod,
    });
    handleClose("integration_connect");
    if (option.emailTo) {
      const subject = encodeURIComponent(option.emailSubject ?? "");
      const body = encodeURIComponent(option.emailBody ?? "");
      window.location.href = `mailto:${option.emailTo}?subject=${subject}&body=${body}`;
      return;
    }

    if (option.oauthPath) {
      window.location.href = `${process.env.NEXT_PUBLIC_API_URL}${option.oauthPath}`;
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      trackEvent("guest_feedback_integration_dialog_opened", {
        initialStep: "integration_options",
      });
      setOpen(true);
      trackEvent("guest_feedback_integration_options_viewed", {
        source: "dialog_opened",
      });
    } else {
      handleClose("dialog_dismissed");
    }
  };

  const handleBackToIntegrationOptions = () => {
    trackEvent("guest_feedback_integration_detail_back_clicked", {
      integrationKey: selectedIntegration?.key ?? null,
      integrationTitle: selectedIntegration?.title ?? null,
    });
    setSelectedIntegration(null);
    trackEvent("guest_feedback_integration_options_viewed", {
      source: "integration_detail_back",
    });
  };

  return (
    <>
      <Button
        onClick={() => handleOpenChange(true)}
        variant={variant}
        size="md"
      >
        <PlusIcon />
        Add integration
      </Button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          {selectedIntegration ? (
            <>
              <DialogHeader>
                <DialogTitle>{selectedIntegration.detailTitle}</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col">
                <Text
                  variant="compact-description"
                  multiline
                  className="text-gray-11"
                >
                  {selectedIntegration.detailDescription}
                </Text>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={handleBackToIntegrationOptions}
                >
                  Back
                </Button>
                <Button onClick={() => handleConnect(selectedIntegration)}>
                  {selectedIntegration.connectLabel}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Select integration type</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-3">
                {INTEGRATION_OPTIONS.map((option) => (
                  <IntegrationOption
                    key={option.key}
                    logo={option.logo}
                    title={option.title}
                    onClick={() => handleSelectIntegration(option)}
                  />
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const IntegrationOption: FC<{
  logo: string;
  title: string;
  onClick: () => void;
}> = ({ logo, title, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-3 rounded-xl border border-gray-2 bg-background p-4 text-left shadow-sm transition-colors hover:bg-gray-1"
    >
      <img src={logo} alt={title} className="size-10 shrink-0 rounded-lg" />
      <Text variant="compact-title">{title}</Text>
    </button>
  );
};

export default AddGuestFeedbackIntegrationButton;
